import slugify from "@sindresorhus/slugify";
import type {TopicItemDto} from "../dibAppApi/dibAppApi";
import {TOPIC_TYPE_PATHS, isTopicTypePathKey} from "../paths";
import {
    buildFileDownloadHref,
    buildFileTopicUrl,
    fileExtensionMap,
    isValidFileType,
    pdfPositionQuery,
} from "../features/content/contentUtils";
import {ResourceType} from "../features/content/types";

export type TopicItemLink = {
    href: string;
    /** Set only for file topics: suggested filename for the download attribute. */
    download?: string;
};

export function getUrlForItem(item: TopicItemDto): TopicItemLink {
    // resourceTypeId arrives as a JSON number (or is absent/null on an old
    // API); ResourceType is string-valued, so convert before the guard.
    // Null/undefined/unknown/non-file all fail the guard → topic path (C4).
    const resourceTypeId =
        item.resourceTypeId == null
            ? undefined
            : (String(item.resourceTypeId) as ResourceType);
    // A PDF takes the topic path and opens in-app.
    if (
        isValidFileType(resourceTypeId) &&
        item.id &&
        resourceTypeId !== ResourceType.pdf
    ) {
        const extension = fileExtensionMap[resourceTypeId] ?? "";
        return {
            href: buildFileDownloadHref(item.id),
            download: `${item.name ?? ""}${extension}`,
        };
    }
    const key = parseInt(item.type ?? "", 10);
    if (isTopicTypePathKey(key)) {
        return {
            href: `/${TOPIC_TYPE_PATHS[key]}/${slugify(item.name ?? "t")}/${encodeURIComponent(item.id?.toLowerCase() ?? "")}`,
        };
    }
    return {href: ""};
}

/**
 * Returns the base URL of the old dib app, picking the dev host when running
 * on localhost or ai.dibdev.no.
 */
export function getDibBaseUrl():
    | "https://dib.dibdev.no"
    | "https://old.dib.no" {
    const hostname = window.location.hostname;
    if (hostname === "localhost" || hostname === "ai.dibdev.no") {
        return "https://dib.dibdev.no";
    }
    return "https://old.dib.no";
}

/**
 * Resolves a backend-emitted content url into an href in this app. The
 * backend owns the path; a host-relative url becomes a root-relative path,
 * and an absolute url passes through for toInternalPath to recognize.
 *
 * Returns "" for a missing url instead of a link to "/undefined".
 */
export function resolveDibHref(url: string | null | undefined): string {
    const trimmed = url?.trim();
    if (!trimmed) return "";
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    // Collapse leading slashes: "//lov/x" would read as a protocol-relative
    // url to the host "lov".
    return `/${trimmed.replace(/^\/+/, "")}`;
}

/**
 * Hosts the dib app is served from. Besides the two vanity hosts, the dev and
 * staging admin APIs emit their App Service hostnames in content links (e.g.
 * the news feed's `link`), so those count as the dib app too — without them
 * every dib link on ai.dibdev.no/staging falls through to a new-tab anchor.
 */
const DIB_APP_HOSTS = new Set([
    "app.dib.no",
    "dib.dibdev.no",
    "app-dib-dev.azurewebsites.net",
    "app-dib-prod-staging.azurewebsites.net",
]);

/**
 * True when a URL points at the dib app: DIB_APP_HOSTS, or our own hostname.
 * Relative hrefs resolve to our own hostname, and locally a dib app served
 * on another port (e.g. https://localhost:44332, the host the news API emits
 * in dev) shares it, so the port is deliberately not pinned.
 */
function isDibAppHost(url: URL): boolean {
    return (
        DIB_APP_HOSTS.has(url.hostname) ||
        url.hostname === window.location.hostname
    );
}

/**
 * Checks whether a URL (full or relative) points at the dib app (isDibAppHost).
 */
export function isDibContentUrl(href: string): boolean {
    return toInternalPath(href) !== null;
}

/**
 * Legacy file URLs emitted by ai-backend always work on app.dib.no
 * (the host they target). The in-app surface is better served by the
 * same-origin /dibappapi/... shape, which the KAI BFF proxies to
 * dib-app-api; ExperimentalLink applies this rewrite unconditionally.
 *
 * Converts (absolute or no-leading-slash relative):
 *   https://app.dib.no/dibapi/document/file?resourceId=<guid>
 *   dibapi/document/file?resourceId=<guid>
 *
 * to:
 *   /dibappapi/api/documents/<guid>/file
 *
 * Returns the input unchanged if it doesn't match the legacy file
 * pattern.
 */
export function rewriteFileUrlForExperimental(href: string): string {
    if (!href) return href;
    let url: URL;
    try {
        url = new URL(href, window.location.origin);
    } catch {
        return href;
    }
    if (!isDibAppHost(url)) {
        return href;
    }
    // Pathname can be "/dibapi/document/file" (absolute input) or
    // "//dibapi/document/file" (no-leading-slash relative input
    // absolutised by the URL parser preserves the double slash).
    const isLegacyFilePath =
        url.pathname === "/dibapi/document/file" ||
        url.pathname.endsWith("/dibapi/document/file");
    if (!isLegacyFilePath) return href;
    const resourceId = url.searchParams.get("resourceId");
    if (!resourceId) return href;
    return `/dibappapi/api/documents/${encodeURIComponent(resourceId)}/file`;
}

/**
 * Converts any dib app URL (see isDibAppHost) to an internal route path.
 *
 * Accepts:
 *   - Full URLs:    https://app.dib.no/tema/some-slug/some-id
 *   - Relative:     tema/some-slug/some-id
 *
 * Returns e.g. `/tema/some-slug/some-id`, or `null` if the URL
 * does not point at a dib app host.
 */
export function toInternalPath(href: string): string | null {
    if (!href) return null;

    try {
        const url = new URL(href, window.location.origin);
        if (!isDibAppHost(url)) return null;
        // BFF-routed paths are not SPA routes — let callers fall through to a
        // plain anchor so the browser hits the BFF proxy instead of <Link>
        // (which would trigger "No routes matched location").
        if (
            url.pathname.startsWith("/dibapi/") ||
            url.pathname.startsWith("/dibappapi/")
        ) {
            return null;
        }
        return `${url.pathname}${url.search}${url.hash}`;
    } catch {
        return null;
    }
}

/**
 * The in-app link to a PDF at its cited position. Uses the backend's url, the
 * PDF's topic page, like any other topic. Falls back to the /file route when
 * the url is not an in-app page, e.g. history saved with the old download link.
 */
export function pdfTopicHref(
    url: string | null | undefined,
    name: string,
    topicId: string,
    position?: {pageIndex?: number | null; y?: number | null}
): string {
    const internalPath = url ? toInternalPath(url) : null;
    if (!internalPath) return buildFileTopicUrl(name, topicId, position);
    const query = pdfPositionQuery(position);
    return query ? `${internalPath}?${query}` : internalPath;
}
